import { useAction, useAtom } from '@reatom/npm-react';
import {
  useSettingsQuery,
  getStormLastPlay,
  useStorageState,
  usePlayerQuery,
  hasVIP,
  useModal,
  Storage,
  t,
} from '@whitesharx/starfall-frontend-core';
import { pushNotification } from '@whitesharx/starfall-frontend-ui-kit';
import { reatomAsync, withDataAtom, withCache, onConnect } from '@reatom/framework';
import { useEvent } from './useEvent';

const STORM_FOR_AD_KEY = 'storm_for_ad';

const fetchStormLastPlayAction = reatomAsync(getStormLastPlay, 'fetchStormLastPlayAction').pipe(
  withDataAtom({ storm: null }),
  withCache({
    staleTime: 10000,
    length: 1,
  })
);

const getTimeToNextStormAction = reatomAsync(async (ctx, timeout?: number) => {
  const lastPlay = await fetchStormLastPlayAction(ctx);

  if (lastPlay.storm && timeout) {
    const nextStormAvailableAt = new Date(lastPlay.storm.lastUpdatedAt).getTime() + timeout;
    return nextStormAvailableAt - Date.now();
  }

  return 0;
}, 'getTimeToNextStormAction').pipe(
  withDataAtom(0),
  withCache({
    staleTime: 10000,
    length: 1,
  })
);

onConnect(getTimeToNextStormAction.dataAtom, getTimeToNextStormAction);

const useStormLimit = () => {
  const { isOpen: isOpenStormLimitModal, open: openStormLimitModal, close: closeStormLimitModal } = useModal();

  const settingsQuery = useSettingsQuery();
  const { data: playerData, isLoading: playerLoading } = usePlayerQuery();

  const [lastPlay] = useAtom(fetchStormLastPlayAction.dataAtom);
  const [lastPlayPendingAtom] = useAtom(fetchStormLastPlayAction.pendingAtom);

  const [timeToNextStorm] = useAtom(getTimeToNextStormAction.dataAtom);
  const [getTimeToNextStormPendingAtom] = useAtom(getTimeToNextStormAction.pendingAtom);
  const getTimeToNextStorm = useAction(getTimeToNextStormAction);
  const fetchStormLastPlay = useAction(fetchStormLastPlayAction);

  const nextStormTimeout = settingsQuery.data?.meta.storm.nextStormTimeout;

  const isLoading =
    Boolean(lastPlayPendingAtom) || Boolean(getTimeToNextStormPendingAtom) || settingsQuery.isLoading || playerLoading;

  const [stormForAd, setStormForAd] = useStorageState<boolean | undefined>(STORM_FOR_AD_KEY, undefined);

  const canStartStorm = useEvent(async () => {
    if (stormForAd || hasVIP(playerData?.player)) {
      return true;
    }

    return (await getTimeToNextStorm(nextStormTimeout)) <= 0;
  });

  const handleAdViewed = async (startStorm: () => void) => {
    const lastStorm = await fetchStormLastPlay();

    if (lastStorm.storm?.ad || Storage.getItem(STORM_FOR_AD_KEY)) {
      setStormForAd(true);
      pushNotification({ type: 'danger', title: t('challenges.already-use-ad') });
      return;
    }

    closeStormLimitModal();
    setStormForAd(true);
    startStorm();
  };

  return {
    isLoading,
    timeToNextStorm,
    canStartStorm,
    stormForAd,
    setStormForAd,
    adAvailable: !lastPlay?.storm?.ad && !stormForAd,
    isOpenStormLimitModal,
    openStormLimitModal,
    closeStormLimitModal,
    handleAdViewed,
  };
};

export default useStormLimit;
